import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Image,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconGripVertical, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { toApiError } from '../apollo/errors';
import { QueryState } from '../components/QueryState';
import {
  AdminCrossPromoAppsDocument,
  AdminDeleteCrossPromoAppDocument,
  AdminReorderCrossPromoAppsDocument,
} from '../graphql/operations';
import { MoreAppsItemModal, type CrossPromoApp } from './MoreAppsItemModal';

const BLANK =
  "data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3C/svg%3E";

/** "More apps for kids" (TAL-68/TAL-63): a CRUD + reorder screen shaped exactly like `CatalogPage`, minus the five-collection tabs — there is only ever one list here. */
export function MoreAppsPage() {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<CrossPromoApp | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [items, setItems] = useState<CrossPromoApp[]>([]);
  const [syncedFrom, setSyncedFrom] = useState<readonly CrossPromoApp[] | null>(null);

  const { data, loading, error } = useQuery(AdminCrossPromoAppsDocument, {
    fetchPolicy: 'cache-and-network',
  });

  // See `CatalogPage` for why this runs during render rather than in an
  // effect: the server's order is the truth, and this local copy only ever
  // leads it by one drag.
  if (data && data.adminCrossPromoApps !== syncedFrom) {
    setSyncedFrom(data.adminCrossPromoApps);
    setItems(data.adminCrossPromoApps);
  }

  const [reorder, { loading: reordering }] = useMutation(AdminReorderCrossPromoAppsDocument);
  const [remove] = useMutation(AdminDeleteCrossPromoAppDocument, {
    refetchQueries: [{ query: AdminCrossPromoAppsDocument }],
    awaitRefetchQueries: true,
  });

  async function onDragEnd(result: DropResult) {
    const to = result.destination?.index;

    if (to === undefined || to === result.source.index) return;

    const next = [...items];
    const [moved] = next.splice(result.source.index, 1);

    if (!moved) return;

    next.splice(to, 0, moved);
    setItems(next);

    try {
      await reorder({ variables: { ids: next.map((item) => item.id) } });
    } catch (thrown) {
      if (data) setItems(data.adminCrossPromoApps);
      notifications.show({
        color: 'red',
        title: t('moreApps.reorderFailed'),
        message: toApiError(thrown as ErrorLike)?.message ?? '',
      });
    }
  }

  function confirmDelete(item: CrossPromoApp) {
    modals.openConfirmModal({
      title: t('moreApps.confirmDelete.title'),
      centered: true,
      children: <Text size="sm">{t('moreApps.confirmDelete.text', { name: item.name })}</Text>,
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        void remove({ variables: { id: item.id } })
          .then(() => {
            notifications.show({ color: 'teal', message: t('moreApps.deleted') });
          })
          .catch((thrown: ErrorLike) => {
            notifications.show({
              color: 'red',
              title: t('moreApps.deleteFailed'),
              message: toApiError(thrown)?.message ?? '',
            });
          });
      },
    });
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>{t('moreApps.title')}</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => {
            setEditing(null);
            setModalOpen(true);
          }}
        >
          {t('moreApps.add')}
        </Button>
      </Group>

      <QueryState loading={loading && !data} error={error}>
        {items.length > 0 && (
          <Text size="sm" c="dimmed">
            {t('moreApps.reorderHint')}
          </Text>
        )}

        <DragDropContext onDragEnd={(result) => void onDragEnd(result)}>
          <Droppable droppableId="more-apps">
            {(droppable) => (
              <Stack
                gap="xs"
                ref={droppable.innerRef}
                {...droppable.droppableProps}
                style={{ opacity: reordering ? 0.7 : 1 }}
              >
                {items.map((item, index) => (
                  <Draggable key={item.id} draggableId={item.id} index={index}>
                    {(draggable) => (
                      <Card
                        withBorder
                        padding="xs"
                        ref={draggable.innerRef}
                        {...draggable.draggableProps}
                      >
                        <Group gap="sm" wrap="nowrap">
                          <div
                            {...draggable.dragHandleProps}
                            aria-label={t('moreApps.move', { name: item.name })}
                            style={{ display: 'flex', cursor: 'grab' }}
                          >
                            <IconGripVertical size={18} />
                          </div>

                          <Image
                            src={item.artworkUrl}
                            alt=""
                            h={40}
                            w={40}
                            radius="sm"
                            fit="contain"
                            fallbackSrc={BLANK}
                          />

                          <Text fw={500} style={{ flex: 1, minWidth: 0 }}>
                            {item.name}
                          </Text>

                          {item.googlePlayUrl ? (
                            <Anchor
                              href={item.googlePlayUrl}
                              target="_blank"
                              rel="noreferrer"
                              size="sm"
                            >
                              {t('moreApps.googlePlay')}
                            </Anchor>
                          ) : (
                            <Badge variant="light" color="gray" size="sm">
                              {t('moreApps.googlePlay')}
                            </Badge>
                          )}

                          {item.appStoreUrl ? (
                            <Anchor
                              href={item.appStoreUrl}
                              target="_blank"
                              rel="noreferrer"
                              size="sm"
                            >
                              {t('moreApps.appStore')}
                            </Anchor>
                          ) : (
                            <Badge variant="light" color="gray" size="sm">
                              {t('moreApps.appStore')}
                            </Badge>
                          )}

                          <Tooltip label={t('common.edit')}>
                            <ActionIcon
                              variant="subtle"
                              aria-label={t('moreApps.edit', { name: item.name })}
                              onClick={() => {
                                setEditing(item);
                                setModalOpen(true);
                              }}
                            >
                              <IconPencil size={18} />
                            </ActionIcon>
                          </Tooltip>

                          <Tooltip label={t('common.delete')}>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              aria-label={t('moreApps.delete', { name: item.name })}
                              onClick={() => {
                                confirmDelete(item);
                              }}
                            >
                              <IconTrash size={18} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Card>
                    )}
                  </Draggable>
                ))}
                {droppable.placeholder}
              </Stack>
            )}
          </Droppable>
        </DragDropContext>

        {items.length === 0 && (
          <Text c="dimmed" ta="center" py="md">
            {t('moreApps.empty')}
          </Text>
        )}
      </QueryState>

      {modalOpen && (
        <MoreAppsItemModal
          key={editing?.id ?? 'new'}
          item={editing}
          opened={modalOpen}
          onClose={() => {
            setModalOpen(false);
          }}
        />
      )}
    </Stack>
  );
}
